import { createContext, useContext, useReducer, useEffect } from 'react';
import {gql} from '@apollo/client';
import {useMutation, useQuery} from '@apollo/client/react';
import { useAuthContext } from '@/context/AuthContext';
import { NetworkConnectionLost } from '@/components/organisms/feedback/NetworkConnectionLost';

// Déclaration des requêtes GraphQL
const GET_NOTIFICATIONS_LAST_KNOWN_UUID = gql`
  query Notifications($lastKnownUuid: ID) {
    notifications(lastKnownUuid: $lastKnownUuid) {
      uuid
      createdAt
      type
      nature
      message
      readStatus
      deepLink
    }
  }
`;
const READ_ALL_NOTIFICATIONS = gql`
  mutation ReadAllNotifications($notificationNature: NotificationNature!) {
    readAllNotifications(notificationNature: $notificationNature)
  }
`;

const READ_NOTIFICATION = gql`
  mutation ReadNotification($uuid: ID!) {
    readNotification(uuid: $uuid)
  }
`;

const DELETE_NOTIFICATION = gql`
  mutation DeleteNotification($uuid: ID!) {
    deleteNotification(uuid: $uuid)
  }
`;

// Initial state pour le reducer
const initialState = {
    notifications: [],
    informations: [],
    actions: [],
    lastKnownUuid: null,
    error: null,
    sortOrders: {
        informations: 'desc',
        actions: 'desc',
    },
};

const sortByCreatedAt = (notifications, order = 'desc') => {
    return [...notifications].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();

        return order === 'asc' ? dateA - dateB : dateB - dateA;
    });
};

// Création du contexte
const notificationReducer = (state, action) => {
    switch (action.type) {
        case 'SET_NOTIFICATIONS': {
            const newNotifications = action.payload;
            const updatedNotifications = state.notifications.map(notification => {
                const updatedNotification = newNotifications.find(newNotif => newNotif.uuid === notification.uuid);
                return updatedNotification || notification;
            });

            // Ajoute les nouvelles notifications qui n'existent pas déjà dans le state
            const newUuids = newNotifications.filter(newNotif =>
                !state.notifications.some(existingNotif => existingNotif.uuid === newNotif.uuid)
            );

            const mergedNotifications = [...updatedNotifications, ...newUuids];
            const sortedNotifications = sortByCreatedAt(mergedNotifications, 'desc');
            const sortedInformations = sortByCreatedAt(
                mergedNotifications.filter(notification => notification.nature === 'NOTIFICATION'),
                state.sortOrders.informations
            );
            const sortedActions = sortByCreatedAt(
                mergedNotifications.filter(notification => notification.nature === 'ACTION'),
                state.sortOrders.actions
            );
            return {
                ...state,
                notifications: sortedNotifications,
                informations: sortedInformations,
                actions: sortedActions,
                lastKnownUuid: sortedNotifications.length > 0 ? sortedNotifications[0].uuid : state.lastKnownUuid
            };
        }
        case 'SORT_NOTIFICATIONS':
            return {
                ...state,
                [action.notificationType]: action.payload,
                sortOrders: {
                    ...state.sortOrders,
                    [action.notificationType]: action.order,
                },
            };
        case 'MARK_AS_READ':
            return {
                ...state,
                notifications: state.notifications.map(notification =>
                    notification.uuid === action.uuid ? { ...notification, readStatus: 'READ' } : { ...notification }
                ),
                informations: state.informations.map(notification =>
                    notification.uuid === action.uuid ? { ...notification, readStatus: 'READ' } : { ...notification }
                ),
                actions: state.actions.map(notification =>
                    notification.uuid === action.uuid ? { ...notification, readStatus: 'READ' } : { ...notification }
                ),
            };
        case 'DELETE_NOTIFICATION': {
            const updatedNotificationsAfterDelete = state.notifications.filter(notification => notification.uuid !== action.uuid);

            // Recalcule du lastKnownUuid après suppression
            const newLastKnownUuid = updatedNotificationsAfterDelete.length > 0
                ? updatedNotificationsAfterDelete[0].uuid  // La notification la plus récente devient la nouvelle lastKnownUuid
                : null;  // Si plus aucune notification, on remet à null

            return {
                ...state,
                notifications: state.notifications.filter(notification => notification.uuid !== action.uuid),
                informations: state.informations.filter(notification => notification.uuid !== action.uuid),
                actions: state.actions.filter(notification => notification.uuid !== action.uuid),
                lastKnownUuid: newLastKnownUuid  // Mise à jour de lastKnownUuid

            };
        }
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload,
            };
        case 'RESET_LAST_KNOWN_UUID':
            return {
                ...state,
                lastKnownUuid: null,  // Réinitialise lastKnownUuid à null
            };
        default:
            return state;
    }
};

const NotificationContext = createContext();

// Provider du contexte
export function NotificationProvider({ children }) {
    // Tous les hooks doivent être appelés à chaque render, pas conditionnellement
    const [state, dispatch] = useReducer(notificationReducer, initialState);
    const { notifications, informations, actions, lastKnownUuid } = state;

    // Hook de requête GraphQL - sera désactivé si non authentifié
    const { loading, error, data, refetch } = useQuery(GET_NOTIFICATIONS_LAST_KNOWN_UUID, {
        variables: { lastKnownUuid },
        fetchPolicy: "cache-and-network",
        onError: (err) => {
            dispatch({
                type: 'SET_ERROR',
                payload: err,
            });
        },
    });

    // Mutations - seront utilisées seulement si authentifié
    const [readNotificationMutation] = useMutation(READ_NOTIFICATION, {

        onError: (err) => {
            console.error("Error marking notification as read:", err);
        },
    });

    const [deleteNotificationMutation] = useMutation(DELETE_NOTIFICATION, {

        onError: (err) => {
            console.error("Error deleting notification:", err);
        },
        onCompleted: () => refetch({ lastKnownUuid: undefined })
    });

    const [readAllNotificationsMutation] = useMutation(READ_ALL_NOTIFICATIONS, {

        onError: (err) => {
            console.error("Error marking all notifications as read:", err);
        },
    });

    // Effect pour traiter les nouvelles données et mettre à jour les états
    useEffect(() => {
        if (data && Array.isArray(data.notifications)) {
            dispatch({
                type: 'SET_NOTIFICATIONS',
                payload: data.notifications,
            });
        }
    }, [data]);

    // Effect pour la récupération périodique des notifications
    useEffect(() => {
        const interval = setInterval(() => {
                refetch();
        }, 15000); // Rafraîchissement toutes les 15 secondes

        return () => clearInterval(interval);
    }, [refetch, lastKnownUuid]);

    // Fonctions pour les actions (seulement utilisées si authentifié)
    const readNotification = async (uuid) => {
        try {
            const { data } = await readNotificationMutation({ variables: { uuid } });
            if (data.readNotification === true) {
                dispatch({ type: 'MARK_AS_READ', uuid });
            }
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
        }
    };

    const deleteNotification = async (uuid) => {
        try {
            const { data } = await deleteNotificationMutation({ variables: { uuid } });
            if (data?.deleteNotification === true) {
                dispatch({ type: 'DELETE_NOTIFICATION', uuid });
            }
        } catch (error) {
            console.error("Failed to delete notification:", error);
        }
    };

    const markAllAsRead = async (type) => {
        try {
            const { data } = await readAllNotificationsMutation({
                variables: { notificationNature: type === 'informations' ? 'NOTIFICATION' : 'ACTION' }
            });
            if (data?.readAllNotifications === true) {
                dispatch({ type: 'RESET_LAST_KNOWN_UUID' });
                refetch({ lastKnownUuid: null });
            }
        } catch (error) {
            console.error("Failed to mark all notifications as read:", error);
        }
    };

    const sortNotifications = (order, type) => {
        const notificationsToSort = type === 'informations' ? informations : actions;
        const sortedNotifications = sortByCreatedAt(notificationsToSort, order);
        dispatch({
            type: 'SORT_NOTIFICATIONS',
            notificationType: type,
            payload: sortedNotifications,
            order,
        });
    };

    // Retourner le provider avec les bonnes valeurs selon l'état d'authentification
    const contextValue = {
        notifications,
        informations,
        actions,
        readNotification,
        deleteNotification,
        markAllAsRead,
        sortNotifications,
        loading,
        error,
        refetch
    };

    return (
        <NotificationContext.Provider value={contextValue}>
            {error?.networkError && <NetworkConnectionLost />}
            {children}
        </NotificationContext.Provider>
    );
}



// Hook pour utiliser le contexte
export const useNotifications = () => useContext(NotificationContext);
